/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateTerminalResetTable,
  updateBinaryCaptureActive,
  updateBinaryCaptureReady,
} from "../../../redux/monitoring/monitoringSlice";
import { useGetBankQuery } from "../../../redux/banks/bankAPI";
// COMPONENTS
import TerminalFilters from "./TerminalFilters";
import TerminalTable from "./TerminalTable";
import TerminalJsonViewer from "./TerminalJsonViewer";
import MonitoringElevatorsFullView from "../fullView/MonitoringElevatorsFullView";
import MonitoringEscalatorsFullView from "../fullView/MonitoringEscalatorsFullView";
import MonitoringMovingWalkFullView from "../fullView/MonitoringMovingWalkFullView";
import TerminalBankFloors from "./TerminalBankFloors";
// INTERFACES
import { RootState } from "../../../app/store";
import {
  LayoutInterface,
  TerminalGlobalObject,
} from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import {
  encodeReplayFile,
  ReplayRecord,
} from "../../../utils/binaryReplayCodec";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { uuidToString } from "../../../utils/uuid";

function TerminalPage({ layouts }: { layouts: LayoutInterface[] }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [terminalBanks, setTerminalBanks] = useState<TerminalGlobalObject[]>(
    []
  );
  const [terminalLifts, setTerminalLifts] = useState<TerminalGlobalObject[]>(
    []
  );
  const [liftNameMap, setLiftNameMap] = useState<Record<string, string>>({});
  const [bank, setBank] = useState<BankInterface>({ ...BLANK_BANK });
  const [bankId, setBankId] = useState<string>("");
  const [liftId, setLiftId] = useState<string>("");
  const [serverId, setServerId] = useState<string>("");
  const [channelId, setChannelId] = useState<string>("");
  const [bankLiftId, setBankLiftId] = useState<string>("");
  const [socketMessages, setSocketMessages] = useState<
    SocketMessageInterface[]
  >([]);
  const [filterType, setFilterType] = useState<"ALL" | "UNIT" | "BANK">("ALL");
  const [token, setToken] = useState<string | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { showTerminalJsonViewer, showTerminalTable, terminalShowBankFloors } =
    useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const { data } = useGetBankQuery(
    { token, bankId: bankId || bankLiftId },
    { skip: !token, refetchOnMountOrArgChange: false }
  );

  // ************************************************ */
  // REFS ****************************************** */
  const capturedMessagesRef = useRef<ReplayRecord[]>([]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickStartCapture = () => {
    capturedMessagesRef.current = [];
    dispatch(updateBinaryCaptureActive(true));
    dispatch(updateBinaryCaptureReady(false));
  };

  const onClickStopCapture = () => {
    dispatch(updateBinaryCaptureActive(false));
    dispatch(updateBinaryCaptureReady(true));
  };

  const onClickDownloadCapture = () => {
    const blob = encodeReplayFile(capturedMessagesRef.current);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `capture_${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/:/g, "-")}.ln2r`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const onClickDownloadJsonFile = () => {
    try {
      const serializable = socketMessages.map((msg) => {
        const { bankId: bid, liftId: lid, ...rest } = msg;
        return {
          ...rest,
          bankId: uuidToString(bid),
          liftId: uuidToString(lid),
        };
      });
      const jsonString = JSON.stringify(serializable, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "replayFile";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      dispatch(updateTerminalResetTable(false));
    };
  }, []);

  useEffect(() => {
    const newBanks: TerminalGlobalObject[] = [];
    const newLifts: TerminalGlobalObject[] = [];
    const nameMap: Record<string, string> = {};
    layouts.forEach((layout: LayoutInterface) => {
      newBanks.push({
        id: layout.idBank,
        name: layout.name,
        channelId: layout.channel_id,
        serverId: layout.idServer,
        bankId: layout.idBank,
        liftId: "",
      });
      if (layout.lifts) {
        layout.lifts.forEach((lift: LiftInterface) => {
          newLifts.push({
            id: lift.id,
            name: `${layout.name} - ${lift.name}`,
            channelId: layout.channel_id,
            serverId: layout.idServer,
            bankId: layout.idBank,
            liftId: lift.id,
          });
          nameMap[lift.id] = lift.name;
        });
      }
    });
    setTerminalBanks(newBanks);
    setTerminalLifts(newLifts);
    setLiftNameMap(nameMap);
  }, [layouts]);

  useEffect(() => {
    if (data && data.data) {
      setBank(data.data);
      // Merge lift names from the bank API response into the name map
      if (data.data.lifts) {
        setLiftNameMap((prev) => {
          const updated = { ...prev };
          let changed = false;
          data.data.lifts.forEach((lift: LiftInterface) => {
            if (lift.id && lift.name && !updated[lift.id]) {
              updated[lift.id] = lift.name;
              changed = true;
            }
          });
          return changed ? updated : prev;
        });
      }
    }
  }, [data]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className={`terminal__drawer__container__${theme}`}>
      <TerminalFilters
        terminalBanks={terminalBanks}
        terminalLifts={terminalLifts}
        setBankId={setBankId}
        bankId={bankId}
        setLiftId={setLiftId}
        setChannelId={setChannelId}
        setServerId={setServerId}
        setBankLiftId={setBankLiftId}
        liftId={liftId}
        filterType={filterType}
        setFilterType={setFilterType}
        onClickDownloadJsonFile={onClickDownloadJsonFile}
        onClickStartCapture={onClickStartCapture}
        onClickStopCapture={onClickStopCapture}
        onClickDownloadCapture={onClickDownloadCapture}
      />
      <br />
      {showTerminalTable && (
        <div className="terminal__drawer__tableContainer">
          <TerminalTable
            liftId={liftId}
            bankId={bankId}
            socketMessages={socketMessages}
            setSocketMessages={setSocketMessages}
            filterType={filterType}
            liftNameMap={liftNameMap}
            capturedMessagesRef={capturedMessagesRef}
          />
        </div>
      )}
      {showTerminalJsonViewer && (
        <div className="generalStyles__flex" style={{ marginTop: "100px" }}>
          <div style={{ marginRight: "20px", width: "100%" }}>
            <div
              className={`monitoring__card__${theme}`}
              style={{
                height: "auto",
                marginTop: "20px",
                paddingTop: "15px",
                marginRight: "15px",
                width: "100%",
              }}
            >
              {bank && bank.id && bank.type === "ELEVATOR" && (
                <MonitoringElevatorsFullView
                  bank={bank}
                  serverId={serverId}
                  channelId={channelId}
                  enableActions={false}
                />
              )}
              {bank && bank.id && bank.type === "ESCALATOR" && (
                <MonitoringEscalatorsFullView
                  bank={bank}
                  serverId={serverId}
                  channelId={channelId}
                  enableActions
                />
              )}
              {bank && bank.id && bank.type === "MOVINGWALK" && (
                <MonitoringMovingWalkFullView
                  bank={bank}
                  serverId={serverId}
                  channelId={channelId}
                />
              )}
            </div>
          </div>
          <div className="generalStyles__width100">
            <TerminalJsonViewer socketMessages={socketMessages} />
          </div>
        </div>
      )}
      {terminalShowBankFloors && <TerminalBankFloors bank={bank} />}
    </div>
  );
}

export default TerminalPage;
